import { SettingsAccordion } from '../../SettingsAccordion';
import { useTranslate } from '@bynder/localization';
import { useCardWidgetMutations } from '../../../../../mutations/widgets/card';
import { useActiveWidget } from '../../../../../queries/guides';
import { CardWidget } from '../../../../../types';
import { useInspectorStore } from '../../../../../stores/inspectorStore';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { InputField, TextareaField, Thumbnail } from '@bynder/design-system';
import { INSPECTOR_DEBOUNCE_DELAY } from '../../constants';
import { IconEdit } from '@bynder/icons';

export const CardTextEditor = () => {
  const { translate } = useTranslate();
  const { editHeadline } = useCardWidgetMutations();
  const activeWidget = useActiveWidget() as CardWidget;
  const widgetSettings = useInspectorStore(s => s.widgetSettings);
  const card = activeWidget?.cards[widgetSettings?.widgetIndex];
  const handleEditTitle = (newTitle: string) => {
    editHeadline.mutate({
      title: newTitle,
      description: card.headline.description,
      cardId: card.id,
      cardIndex: widgetSettings?.widgetIndex,
    });
  };
  const handleEditDescription = (newDescription: string) => {
    editHeadline.mutate({
      title: card.headline.title,
      description: newDescription,
      cardId: card.id,
      cardIndex: widgetSettings?.widgetIndex,
    });
  };
  return (
    <SettingsAccordion
      thumbnail={<Thumbnail icon={<IconEdit />} />}
      title={translate('WIDGET_INSPECTOR.CARD_CUSTOMIZE_ACCORDION')}
    >
      <InspectorInput
        text={card?.headline.title}
        placeholder={translate('WIDGET_INSPECTOR.CARD_COLOR_TITLE')}
        handleChange={handleEditTitle}
      />
      <InspectorInput
        text={card?.headline.description}
        placeholder={translate('WIDGET_INSPECTOR.CARD_COLOR_DESCRIPTION')}
        handleChange={handleEditDescription}
        isTextArea
      />
    </SettingsAccordion>
  );
};
const InspectorInput = ({
  text,
  label,
  placeholder,
  handleChange,
  isTextArea = false,
}: {
  text: string;
  label?: string;
  placeholder: string;
  handleChange: (newTitle: string) => void;
  isTextArea?: boolean;
}) => {
  const [stateText, setStateText] = useState(text);

  const onSetInput = (newTitle: string) => {
    return handleChange(newTitle);
  };

  const debouncedSetTitle = useDebouncedCallback(
    onSetInput,
    INSPECTOR_DEBOUNCE_DELAY,
  );

  const handleTitleChange = (value: string) => {
    setStateText(value);
    debouncedSetTitle(value);
  };

  const handleOnBlur = () => {
    debouncedSetTitle.cancel();
  };

  useEffect(() => {
    setStateText(text);
  }, [text]);

  return isTextArea ? (
    <TextareaField
      data-testid="cardDescription"
      onBlur={handleOnBlur}
      label={label}
      placeholder={placeholder}
      value={stateText}
      onChange={handleTitleChange}
    />
  ) : (
    <InputField
      data-testid="cardTitle"
      onBlur={handleOnBlur}
      label={label}
      placeholder={placeholder}
      value={stateText}
      onChange={handleTitleChange}
    />
  );
};
